import { useEffect, useRef, useState } from 'react'
import { ConfirmAction } from '@/components/confirm-action'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, formatMb } from '@/lib/utils'
import {
  PLATFORM_LABEL,
  PLATFORMS,
  mandatoryRuleInputSchema,
  mergePatchMessage,
  useDeleteMandatoryRule,
  useMandatoryRules,
  usePatchMessage,
  useSaveMandatoryRule,
  useSavePatchMessage,
  useStablePatches,
  useTranslateDraft,
  type MandatoryRule,
  type Platform,
} from '@/features/app-patch-policy/api'
import { useLocale } from '@/features/app-patch-policy/locale'
import { MessageTranslationsDialog } from '@/features/app-patch-policy/message-translations-dialog'

// Keyed "release_version:patch_number" — patch numbers restart at 1 per release, so the number
// alone would collide across releases as a picker value.
function stablePatchKey(p: { release_version: string; patch_number: number }) {
  return `${p.release_version}:${p.patch_number}`
}

type RuleOutcome = {
  platform: Platform
  ruleSkipped?: boolean
  ruleUnverified?: boolean
  ruleError?: string
  messageSaved?: boolean
  messageError?: string
}

/**
 * Section A: the mandatory Shorebird-patch floor. One merged list + one form across both
 * platforms, instead of a platform-tab-switcher. A platform can only be checked once the shared
 * release_version + patch_number is actually live on *that* platform's stable (Shorebird tracks
 * releases per platform, and the server would reject a mismatch anyway). The message field rides
 * along in the same submit, scoped to this exact release_version+patch_number: blank means "leave
 * whatever this exact patch already has", not "clear it".
 */
export function MandatoryRulesCard() {
  const { t } = useLocale()

  // One hook call per platform — hooks can't be called conditionally or in a loop.
  const iosRules = useMandatoryRules('ios')
  const androidRules = useMandatoryRules('android')
  const rulesByPlatform: Record<Platform, typeof iosRules> = { ios: iosRules, android: androidRules }

  const iosStable = useStablePatches('ios')
  const androidStable = useStablePatches('android')
  const stableByPlatform: Record<Platform, typeof iosStable> = { ios: iosStable, android: androidStable }

  const saveIos = useSaveMandatoryRule('ios')
  const saveAndroid = useSaveMandatoryRule('android')
  const saveByPlatform: Record<Platform, typeof saveIos> = { ios: saveIos, android: saveAndroid }

  const removeIos = useDeleteMandatoryRule('ios')
  const removeAndroid = useDeleteMandatoryRule('android')
  const removeByPlatform: Record<Platform, typeof removeIos> = { ios: removeIos, android: removeAndroid }

  const saveMessage = useSavePatchMessage()

  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([])
  const [releaseVersion, setReleaseVersion] = useState('')
  const [patchNumber, setPatchNumber] = useState(0)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [translationsOpen, setTranslationsOpen] = useState(false)

  // One message query per platform, scoped to whatever release_version+patch_number is currently
  // picked — this is the exact patch a save would write to, so it's also what "blank means keep
  // this" merges against, and what the prefill effect below shows staff before they type anything.
  const iosMessage = usePatchMessage('ios', releaseVersion, patchNumber)
  const androidMessage = usePatchMessage('android', releaseVersion, patchNumber)
  const messageByPlatform: Record<Platform, typeof iosMessage> = { ios: iosMessage, android: androidMessage }
  const [outcomes, setOutcomes] = useState<RuleOutcome[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pendingClear, setPendingClear] = useState<MandatoryRule | null>(null)

  const browsePlatform = selectedPlatforms[0] ?? null
  // No default-platform fallback: the picker this feeds only renders once browsePlatform is set.
  const stablePatchesQuery = browsePlatform ? stableByPlatform[browsePlatform] : null
  const translateDraft = useTranslateDraft(browsePlatform ?? 'ios', releaseVersion, patchNumber)
  // A real release+patch must be picked before there is anything to translate against or a
  // patch-scoped message to edit.
  const patchSelected = releaseVersion.trim() !== '' && patchNumber > 0

  function platformHasVersion(platform: Platform) {
    if (releaseVersion.trim() === '') return true
    return (
      stableByPlatform[platform].data?.some(
        (p) => p.release_version === releaseVersion && p.patch_number === patchNumber,
      ) ?? false
    )
  }

  // Drop a checked platform the instant the shared release/patch no longer matches its own stable list.
  useEffect(() => {
    setSelectedPlatforms((prev) => prev.filter(platformHasVersion))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseVersion, patchNumber, iosStable.data, androidStable.data])

  // Show whatever this exact release_version+patch_number already has the moment one is picked —
  // without this, splitting the message out per-patch would make an already-saved message
  // invisible until staff happened to retype it, since there's no more "one persistent row per
  // platform" to read it from at a glance.
  // Prefills once per distinct (platform, release, patch), so a late refetch never stomps what
  // staff already started typing.
  const prefilledKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!browsePlatform || !patchSelected) return
    const platformMessage = messageByPlatform[browsePlatform]
    if (!platformMessage.isSuccess) return
    const key = `${browsePlatform}:${releaseVersion}:${patchNumber}`
    if (prefilledKeyRef.current === key) return
    prefilledKeyRef.current = key
    setTitle(platformMessage.data?.title ?? '')
    setMessage(platformMessage.data?.message ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePlatform, releaseVersion, patchNumber, browsePlatform ? messageByPlatform[browsePlatform].isSuccess : null, browsePlatform ? messageByPlatform[browsePlatform].data : null])

  function togglePlatform(platform: Platform, checked: boolean) {
    setSelectedPlatforms((prev) => (checked ? [...prev, platform] : prev.filter((p) => p !== platform)))
  }

  const selectedStablePatch = stablePatchesQuery?.data?.find(
    (p) => p.release_version === releaseVersion && p.patch_number === patchNumber,
  )
  const stablePatchSelectValue = selectedStablePatch ? stablePatchKey(selectedStablePatch) : undefined

  function pickStablePatch(key: string) {
    const patch = stablePatchesQuery?.data?.find((p) => stablePatchKey(p) === key)
    if (!patch) return
    setReleaseVersion(patch.release_version)
    setPatchNumber(patch.patch_number)
  }

  const validInput = mandatoryRuleInputSchema.safeParse({
    release_version: releaseVersion,
    patch_number: patchNumber,
  }).success
  // Submit waits only while a selected platform's message query is still loading (not forever on
  // error) — see the warning shown below when one has errored out.
  const messageQueriesPending = patchSelected && selectedPlatforms.some((p) => messageByPlatform[p].isPending)
  const messageQueriesErrored = patchSelected && selectedPlatforms.some((p) => messageByPlatform[p].isError)

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    setOutcomes(null)
    setSubmitting(true)
    const results = await Promise.all(
      selectedPlatforms.map(async (platform) => {
        const outcome: RuleOutcome = { platform }

        const existingRule = rulesByPlatform[platform].data?.find((r) => r.release_version === releaseVersion)
        if (existingRule && existingRule.patch_number === patchNumber) {
          outcome.ruleSkipped = true
        } else {
          try {
            const saved = await saveByPlatform[platform].mutateAsync({
              release_version: releaseVersion,
              patch_number: patchNumber,
            })
            outcome.ruleUnverified = saved.unverified === true
          } catch (err) {
            outcome.ruleError = errorMessage(err, 'Failed to save.')
          }
        }

        // Blank means "leave whatever this exact release_version+patch_number already has" — same
        // convention for both fields. The server has no such merge of its own (a PUT here sets
        // title/message to exactly what's in the body), so an untouched field is resent as its own
        // current value (from this platform's usePatchMessage query) rather than omitted.
        // `?? null` normalises a missing row so an unset field never reads as changed.
        const current = messageByPlatform[platform].data
        const merged = mergePatchMessage(current, title, message)
        if ((current?.title ?? null) !== merged.title || (current?.message ?? null) !== merged.message) {
          try {
            await saveMessage.mutateAsync({
              platform,
              release_version: releaseVersion,
              patch_number: patchNumber,
              input: merged,
            })
            outcome.messageSaved = true
          } catch (err) {
            outcome.messageError = errorMessage(err, 'Failed to save message.')
          }
        }

        return outcome
      }),
    )
    setSubmitting(false)
    setOutcomes(results)
    if (!results.some((r) => r.ruleError || r.messageError)) {
      setSelectedPlatforms([])
      setReleaseVersion('')
      setPatchNumber(0)
      setTitle('')
      setMessage('')
      // Without this, re-picking the same patch after a successful save is blocked from
      // prefilling again — its key never changed — and shows stale/blank fields instead.
      prefilledKeyRef.current = null
    }
  }

  const mergedRules = [...(iosRules.data ?? []), ...(androidRules.data ?? [])].sort(
    (a, b) => a.release_version.localeCompare(b.release_version) || a.platform.localeCompare(b.platform),
  )
  const rulesLoading = iosRules.isPending || androidRules.isPending
  const rulesErrored = iosRules.isError || androidRules.isError

  return (
    <div className="space-y-3">
      <Label>A · {t('rules.heading')}</Label>
      <p className="text-xs text-faint">{t('rules.help')}</p>

      {rulesLoading && <Skeleton className="h-9 w-full rounded-md" />}
      {rulesErrored && <p className="text-xs text-destructive">{t('rules.loadError')}</p>}
      {!rulesLoading && !rulesErrored && mergedRules.length === 0 && (
        <p className="text-xs text-muted-foreground">{t('rules.noneAny')}</p>
      )}
      {mergedRules.length > 0 && (
        <ul className="space-y-1.5">
          {mergedRules.map((rule) => (
            <li
              key={`${rule.platform}-${rule.release_version}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span>
                <span className="font-medium">{PLATFORM_LABEL[rule.platform]}</span> ·{' '}
                <span className="font-medium">{rule.release_version}</span> — patch {rule.patch_number}+
                {rule.patch_size_bytes != null && ` (${formatMb(rule.patch_size_bytes)} MB)`}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={removeByPlatform[rule.platform].isPending}
                onClick={() => setPendingClear(rule)}
              >
                {t('common.clear')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form className="space-y-3 rounded-md border border-border p-3" onSubmit={onSubmit}>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <Label>{t('rules.platformLabel')}</Label>
            <span className="text-[11px] text-faint">{t('rules.multiSelectHint')}</span>
          </div>
          <div className="flex flex-col gap-2">
            {PLATFORMS.map((p) => {
              const disabled = !platformHasVersion(p)
              return (
                <label
                  key={p}
                  className={`flex items-center gap-2 text-sm ${disabled ? 'text-muted-foreground' : ''}`}
                >
                  <Checkbox
                    checked={selectedPlatforms.includes(p)}
                    disabled={disabled}
                    onCheckedChange={(value) => togglePlatform(p, Boolean(value))}
                  />
                  {PLATFORM_LABEL[p]}
                </label>
              )
            })}
          </div>
          {releaseVersion.trim() !== '' && PLATFORMS.some((p) => !platformHasVersion(p)) && (
            <p className="text-xs text-faint">{t('rules.unavailableHint')}</p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="rule-picker" className="text-xs">
              {t('rules.pickFromStable')}
            </Label>
            {browsePlatform ? (
              <Select
                value={stablePatchSelectValue}
                onValueChange={pickStablePatch}
                disabled={stableByPlatform[browsePlatform].isPending || stableByPlatform[browsePlatform].isError}
              >
                <SelectTrigger id="rule-picker" className="w-48">
                  <SelectValue
                    placeholder={
                      stableByPlatform[browsePlatform].isPending
                        ? t('rules.pickerLoading')
                        : stableByPlatform[browsePlatform].data?.length === 0
                          ? t('rules.pickerEmpty')
                          : t('rules.pickerPrompt')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {stableByPlatform[browsePlatform].data?.map((p) => (
                    <SelectItem key={stablePatchKey(p)} value={stablePatchKey(p)}>
                      {p.release_version} — patch {p.patch_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="w-48 text-xs text-muted-foreground">{t('rules.checkPlatformFirst')}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="rule-release" className="text-xs">
              {t('rules.releaseVersionLabel')}
            </Label>
            <Input
              id="rule-release"
              placeholder={t('rules.releaseVersionPlaceholder')}
              className="w-36"
              value={releaseVersion}
              onChange={(event) => setReleaseVersion(event.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rule-patch-number" className="text-xs">
              {t('rules.patchNumberLabel')}
            </Label>
            <Input
              id="rule-patch-number"
              type="number"
              min={0}
              step={1}
              className="w-24"
              value={patchNumber || ''}
              onChange={(event) => setPatchNumber(event.target.value === '' ? 0 : Number(event.target.value))}
            />
          </div>
        </div>

        {messageQueriesErrored && (
          <p className="text-xs text-warning">
            Could not load the existing title/message for one or more selected platforms —
            submitting now will overwrite whichever field you leave blank. Reload, or fill in both
            fields to be safe.
          </p>
        )}

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="rule-title" className="text-xs">
              {t('message.titleLabel')}
            </Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!browsePlatform || !patchSelected}
              onClick={() => setTranslationsOpen(true)}
            >
              {t('common.translations')}
            </Button>
          </div>
          <Input
            id="rule-title"
            placeholder={t('message.titlePlaceholder')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={64}
          />
          <p className="text-xs text-faint">{t('message.titleHelper')}</p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="rule-message" className="text-xs">
              {t('message.label')}
            </Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!browsePlatform || !patchSelected}
              onClick={() => setTranslationsOpen(true)}
            >
              {t('common.translations')}
            </Button>
          </div>
          <Textarea
            id="rule-message"
            rows={2}
            placeholder={t('message.placeholder')}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onBlur={(event) => {
              if (!browsePlatform || !patchSelected) return
              const value = event.target.value.trim()
              if (value) translateDraft.mutate({ title: title.trim(), message: value })
            }}
          />
          <p className="text-xs text-faint">{t('message.helper')}</p>
        </div>

        {browsePlatform && patchSelected && translationsOpen && (
          <MessageTranslationsDialog
            platform={browsePlatform}
            releaseVersion={releaseVersion}
            patchNumber={patchNumber}
            englishMessage={messageByPlatform[browsePlatform].data?.message ?? ''}
            open={translationsOpen}
            onOpenChange={setTranslationsOpen}
          />
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={selectedPlatforms.length === 0 || !validInput || submitting || messageQueriesPending}
          >
            {submitting ? t('common.saving') : t('rules.addOrReplace')}
          </Button>
        </div>

        {outcomes && (
          <div className="space-y-0.5">
            {outcomes.map((outcome) => (
              <p
                key={outcome.platform}
                className={`text-xs ${outcome.ruleError || outcome.messageError ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {PLATFORM_LABEL[outcome.platform]}:{' '}
                {outcome.ruleError ? outcome.ruleError : outcome.ruleSkipped ? t('rules.unchanged') : t('rules.saved')}
                {outcome.ruleUnverified && <span className="text-warning"> {t('rules.unverified')}</span>}
                {outcome.messageSaved && ` ${t('message.alsoSaved')}`}
                {outcome.messageError && ` ${t('message.saveFailed')}: ${outcome.messageError}`}
              </p>
            ))}
          </div>
        )}
      </form>
      <p className="text-xs text-warning">{t('rules.warning')}</p>

      <ConfirmAction
        open={pendingClear !== null}
        onOpenChange={(open) => !open && setPendingClear(null)}
        title="Clear this mandatory floor?"
        description={
          pendingClear && (
            <>
              Removes the {PLATFORM_LABEL[pendingClear.platform]} floor for {pendingClear.release_version} (patch{' '}
              {pendingClear.patch_number}+). Devices on this release will no longer be forced to update.
            </>
          )
        }
        confirmLabel="Clear Floor"
        destructive
        onConfirm={() => removeByPlatform[pendingClear!.platform].mutateAsync(pendingClear!.release_version)}
      />
    </div>
  )
}
